import { receiptColumns } from '../data/billTemplates';
import { fieldsOf, ITEM_COLUMNS, TOTAL_PARTS } from '../data/billFields';
import { locationLabelOf, findUserById } from '../data/store';
import { billNumberOf } from './billNumber';

/**
 * Lays out one bill, once, for every way it can be printed.
 *
 * The layout used to be written three times over — ESC/POS bytes for the
 * thermal printers, characters for the preview, and HTML for the browser bill.
 * Three copies of one set of rules drift apart, and these had: the printer's
 * item columns were fixed at 28/6/14 and overflowed a 58mm line, and the
 * printer dropped zero tax lines that the screen showed. Both were reported as
 * bugs by a shop looking at paper that disagreed with the screen.
 *
 * So layout happens here and nowhere else. It returns blocks — text already
 * padded to the paper width — and each output walks the same blocks. A new
 * field type is added in one place and appears everywhere.
 *
 * Blocks:
 *   { kind: 'line', text, align, bold, big }
 *   { kind: 'divider' }
 *   { kind: 'blank' }
 *   { kind: 'qr' | 'barcode', data, caption }
 */

const pad = {
  left: (s, n) => String(s).padEnd(n, ' '),
  right: (s, n) => String(s).padStart(n, ' '),
};

/** Breaks a long value across lines. Half an address is no use to a rider. */
const wrap = (text, len) => {
  const out = [];
  let line = '';
  String(text).split(/\s+/).filter(Boolean).forEach((word) => {
    if (!line.length) line = word.slice(0, len);
    else if (line.length + 1 + word.length <= len) line += ` ${word}`;
    else { out.push(line); line = word.slice(0, len); }
  });
  if (line.length) out.push(line);
  return out.length ? out : [''];
};

const money = (n) => (Number(n) || 0).toFixed(2);
const paise = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const isLineInclusive = (l) => {
  if (String(l?.taxMode || '').trim().toLowerCase() === 'inclusive') return true;
  if (l?.inclusive === true) return true;
  const qty = Number(l?.qty) || 0;
  const price = Number(l?.price) || 0;
  const gross = qty * price;
  const base = Number(l?.base) || 0;
  const slab = Number(l?.gstRate) || 0;
  if (slab > 0 && gross > 0 && base > 0 && base < gross - 0.005) {
    const expectedBase = (gross * 100) / (100 + slab);
    if (Math.abs(base - expectedBase) < 0.05 || Math.abs(gross - base - (Number(l?.lineTax) || 0)) < 0.05) {
      return true;
    }
  }
  return false;
};

/**
 * The branch a bill was rung up at.
 *
 * `branchName` wins where it is set, which is how the sample bill behind the
 * previews names a branch it does not have an id for — a preview of a design
 * with a branch line would otherwise show an empty gap.
 */
const branchNameOf = (bill) =>
  bill.branchName || (bill.locationId ? locationLabelOf(bill.locationId) : '');

const timeOf = (bill) => {
  if (!bill.createdAt) return '';
  const d = new Date(bill.createdAt);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString();
};

const countsOf = (bill) => {
  const lines = bill.lines || [];
  return {
    lines: lines.length,
    qty: lines.reduce((s, l) => s + (Number(l.qty) || 0), 0),
  };
};

/** Fills {billNo}, {total} and the rest into a shop's own line. */
const fillTokens = (text, org, bill) => {
  const counts = countsOf(bill);
  const values = {
    '{shop}': org.name || '',
    '{branch}': branchNameOf(bill),
    '{billNo}': billNumberOf(bill),
    '{date}': (bill.date || '').slice(0, 10),
    '{time}': timeOf(bill),
    '{customer}': bill.customer || 'Walk-in',
    '{phone}': bill.phone || '',
    '{staff}': findUserById(bill.userId)?.name || '',
    '{orderType}': bill.orderType || '',
    '{items}': String(counts.lines),
    '{qty}': String(counts.qty),
    '{subtotal}': money(bill.subtotal),
    '{total}': money(bill.total),
  };
  return String(text || '').replace(/\{[a-zA-Z]+\}/g, (m) => (m in values ? values[m] : m));
};

export const composeReceipt = (org, bill, settings = {}) => {
  const cols = receiptColumns(settings.paperWidth, settings.charsPerLine);
  const { width } = cols;
  const blocks = [];

  const line = (text, f = {}) => {
    // A field's own alignment applies to each wrapped line, so a long centred
    // value stays centred rather than only its first line.
    wrap(text, width).forEach((t) => blocks.push({
      kind: 'line', text: t, align: f.align || 'left', bold: !!f.bold, big: !!f.big,
    }));
  };
  // Rows are padded here, so alignment is already baked in and every output
  // shows the same columns.
  const row = (text, f = {}) => blocks.push({
    kind: 'line', text, align: 'left', bold: !!f.bold, big: !!f.big,
  });

  const labelled = (label, value, f) => {
    if (!value) return;
    line(`${label}: ${value}`, f);
  };

  fieldsOf(settings).forEach((f) => {
    if (f.on === false) return;

    switch (f.type) {
      // A typed value wins over the organization record, so a shop can trade
      // under one name and bill under another.
      case 'shopName': line((f.text || org.name || '').toUpperCase(), f); break;
      case 'branch': { const n = f.text || branchNameOf(bill); if (n) line(n, f); break; }
      case 'address': { const a = f.text || org.address; if (a) line(a, f); break; }
      case 'phone': {
        const p = f.text || org.phone || org.mobileNumber;
        if (p) line(`Ph: ${p}`, f);
        break;
      }
      case 'gstin': if (org.gstin) line(`GSTIN: ${org.gstin}`, f); break;

      case 'title': if (f.text) line(f.text, f); break;
      case 'billNo': line(`Bill No: ${billNumberOf(bill)}`, f); break;
      case 'date': if (bill.date) line(bill.date.slice(0, 10), f); break;
      case 'time': { const t = timeOf(bill); if (t) line(t, f); break; }
      case 'customer': line(`Customer: ${bill.customer || 'Walk-in'}`, f); break;
      case 'customerPhone': labelled('Ph', bill.phone, f); break;
      case 'paymentMode': labelled('Mode', (bill.method || '').toUpperCase(), f); break;
      case 'orderType': labelled('Order', bill.orderType, f); break;
      case 'servedBy': labelled('Served by', findUserById(bill.userId)?.name, f); break;
      case 'deliveryAddress':
        if (bill.deliveryAddress) {
          line('Deliver to:', f);
          line(bill.deliveryAddress, f);
        }
        break;

      case 'items': {
        // The item name takes whatever the chosen columns leave. Dropping the
        // amount column is what turns this into a kitchen ticket, and the name
        // widening to fill the gap is what makes one readable across a counter.
        const chosen = (f.columns || []).filter((c) => ITEM_COLUMNS[c]);
        const used = chosen.reduce((n, c) => n + (ITEM_COLUMNS[c].width === 'qty' ? cols.qtyCol : cols.amtCol), 0);
        const nameCol = Math.max(8, width - used);
        row(
          pad.left(org.type === 'service' ? 'SERVICE' : 'ITEM', nameCol)
          + chosen.map((c) => pad.right(
            ITEM_COLUMNS[c].label.toUpperCase(),
            ITEM_COLUMNS[c].width === 'qty' ? cols.qtyCol : cols.amtCol,
          )).join(''),
        );

        const hasInclusive = (bill.lines || []).some((l) => isLineInclusive(l));
        const hasExclusive = (bill.lines || []).some((l) => !isLineInclusive(l));
        const isMixed = hasInclusive && hasExclusive;

        (bill.lines || []).forEach((l) => {
          let name = l.name || '';
          if (l.label) name += ` (${l.label})`;
          if (name.length > nameCol) name = `${name.slice(0, Math.max(1, nameCol - 2))}..`;

          const cells = chosen.map((c) => {
            const w = ITEM_COLUMNS[c].width === 'qty' ? cols.qtyCol : cols.amtCol;
            if (c === 'qty') return pad.right(String(l.qty), w);
            if (c === 'rate') return pad.right(money(l.price), w);
            return pad.right(money(l.lineTotal), w);
          });
          row(pad.left(name, nameCol) + cells.join(''));

          if (isMixed) {
            const incl = isLineInclusive(l);
            const modeTag = incl ? '[Tax Inclusive]' : '[Tax Exclusive]';
            row(pad.left(`  ${modeTag}`, width));
          }
        });
        break;
      }

      case 'itemCount': {
        const counts = countsOf(bill);
        const value = f.mode === 'qty' ? counts.qty : counts.lines;
        line(`${f.text || 'Total items'}: ${value}`, f);
        break;
      }

      case 'totals': {
        // Each amount sits in the same column the item amounts do, with the
        // label right-aligned against it. Right-aligning the whole string
        // instead — "Subtotal: 145.00", "CGST: 13.05" — lined up the numbers
        // but left the labels staggered down the page, which is what made the
        // foot of the bill look thrown together.
        // Whether this bill's tax came out of its prices rather than being
        // added to them. Read off the lines, which record how they were
        // actually billed, not off the shop's setting today - a receipt
        // reprinted after the shop changed its mind must still describe the
        // sale that happened.
        //
        // Untaxed lines are ignored: a zero-rated line is neither, and one
        // bag of loose rice should not stop an MRP bill saying what it is.
        const taxedLines = (bill.lines || []).filter((l) => Number(l.gstRate) > 0);
        const inclusiveBill = taxedLines.length > 0 && taxedLines.every(
          (l) => String(l.taxMode || '').trim().toLowerCase() === 'inclusive',
        );
        const labelCol = Math.max(6, width - cols.amtCol);
        const amountRow = (label, value, opts = {}) => {
          // A double-height line cannot be padded: the printer draws it at
          // twice the width, so 48 padded characters would run to 96 and wrap.
          // It is right-aligned instead, which lands on the same right margin.
          if (opts.big) {
            line(`${label}: ${money(value)}`, { ...f, align: 'right', bold: true, big: true });
            return;
          }
          row(pad.right(`${label}:`, labelCol) + pad.right(money(value), cols.amtCol), f);
        };

        const lines = bill.lines || [];
        let subtotalVal = Number(bill.subtotal) || 0;
        if (!subtotalVal && lines.length > 0) {
          subtotalVal = lines.reduce((s, l) => s + (Number(l.base) || 0), 0);
        }
        subtotalVal = paise(subtotalVal);

        (f.parts || []).forEach((part) => {
          if (!TOTAL_PARTS[part]) return;
          // Tax lines print even at zero. Omitting them left a shop unable to
          // tell "this product is not taxed" from "the GST line failed to
          // print" — and on a tax invoice a stated zero is the useful answer.
          if (part === 'igst' && !bill.totalIgst) return;
          if (part === 'roundOff') {
            // Read off the bill rather than worked out from it.
            //
            // It used to be `Math.round(total) - total`, which on a bill whose
            // total is already the rounded figure is always zero - so the line
            // never printed, and the paise between the goods and the money
            // taken went unexplained on every receipt that had any.
            //
            // The server records what it moved. A reprint a year from now says
            // what that sale actually did rather than what it would do today.
            const off = Number(bill.roundOff) || 0;
            if (!off) return;
            amountRow('Round off', off);
            return;
          }
          const amounts = {
            subtotal: subtotalVal,
            cgst: bill.totalCgst,
            sgst: bill.totalSgst,
            igst: bill.totalIgst,
            total: bill.total,
          };
          if (part === 'total') {
            // Set apart from the tax lines above it. On a bill the total is the
            // one number a customer looks for.
            blocks.push({ kind: 'blank' });
            amountRow(TOTAL_PARTS[part], amounts[part], { big: f.big !== false });
            // A tax-inclusive bill has to say so. The CGST and SGST lines
            // above came *out* of this total rather than adding to it, and a
            // customer checking the arithmetic without being told would make
            // it come to more than they handed over.
            if (inclusiveBill) {
              line('Prices are inclusive of GST', { align: 'center' });
            }
            return;
          }
          amountRow(TOTAL_PARTS[part], amounts[part]);
        });
        break;
      }

      case 'gstSummary': {
        const bySlab = new Map();
        (bill.lines || []).forEach((l) => {
          const slab = Number(l.gstRate) || 0;
          if (slab <= 0) return;
          const at = bySlab.get(slab) || {
            base: 0, cgst: 0, sgst: 0, igst: 0,
            inclGross: 0, exclBase: 0, exclCgst: 0, exclSgst: 0, exclIgst: 0,
            hasInclusive: false,
          };
          const qty = Number(l.qty) || 0;
          const price = Number(l.price) || 0;
          const gross = qty * price;
          const incl = isLineInclusive(l);

          const given = (v, or0) => (v !== undefined && v !== null ? Number(v) || 0 : or0);
          const tax = given(l.lineTax, incl ? gross - (gross * 100) / (100 + slab) : (gross * slab) / 100);
          const igst = given(l.igst, 0);
          const saidC = given(l.cgst, -1);
          const saidS = given(l.sgst, -1);
          const trust = saidC >= 0 && saidS >= 0 && Math.abs(saidC + saidS - tax) < 0.005;
          const cgst = igst > 0 ? 0 : (trust ? saidC : tax / 2);
          const sgst = igst > 0 ? 0 : (trust ? saidS : (tax - cgst));
          const base = Number(l.base) ? paise(l.base) : (incl ? paise((gross * 100) / (100 + slab)) : gross);

          if (incl) {
            at.inclGross += gross;
            at.hasInclusive = true;
          } else {
            at.exclBase += base;
            at.exclCgst += cgst;
            at.exclSgst += sgst;
            at.exclIgst += igst;
          }
          at.base += base;
          at.cgst += cgst;
          at.sgst += sgst;
          at.igst += igst;
          bySlab.set(slab, at);
        });

        if (!bySlab.size) break;
        const slabs = [...bySlab.keys()].sort((x, y) => x - y);
        const split = [...bySlab.values()].some((v) => v.igst > 0);

        const rows5 = slabs.map((slab) => {
          const v = bySlab.get(slab);
          const dispC = Number(money(v.cgst));
          const dispS = Number(money(v.sgst));
          const dispI = Number(money(v.igst));
          const dispTax = split ? dispI : (dispC + dispS);

          let dispBase;
          if (v.hasInclusive) {
            const exclTax = split ? Number(money(v.exclIgst)) : (Number(money(v.exclCgst)) + Number(money(v.exclSgst)));
            const inclTax = dispTax - exclTax;
            const inclBase = v.inclGross - inclTax;
            dispBase = v.exclBase + inclBase;
          } else {
            dispBase = v.base;
          }

          return {
            rate: `${+slab.toFixed(2)}%`,
            base: money(dispBase),
            a: split ? money(dispI) : money(dispC),
            b: money(dispS),
          };
        });

        const widest = (k, head) => rows5.reduce((m, r) => Math.max(m, r[k].length), head.length);
        const R = widest('rate', 'RATE');
        const T = widest('base', 'TAXABLE');
        const C = widest('a', split ? 'IGST' : 'CGST');
        const S = split ? 0 : widest('b', 'SGST');
        const GAP = '  ';

        const lineOf = (rt, tx, x, y) => (split
          ? pad.right(rt, R) + GAP + pad.left(tx, T) + GAP + pad.left(x, C)
          : pad.right(rt, R) + GAP + pad.left(tx, T) + GAP + pad.left(x, C) + GAP + pad.left(y, S)
        );

        line('GST SUMMARY', { bold: true });
        row(lineOf('RATE', 'TAXABLE', split ? 'IGST' : 'CGST', 'SGST'), { bold: true });
        rows5.forEach((r) => {
          row(lineOf(r.rate, r.base, r.a, r.b));
        });
        break;
      }

      case 'qr':
      case 'barcode': {
        const data = fillTokens(f.url, org, bill).trim();
        if (!data) break;
        blocks.push({ kind: f.type, data, caption: fillTokens(f.text, org, bill) });
        break;
      }

      case 'text': {
        const t = fillTokens(f.text, org, bill);
        if (t.trim()) line(t, f);
        break;
      }

      case 'divider': blocks.push({ kind: 'divider', width }); break;
      case 'blank': blocks.push({ kind: 'blank' }); break;
      default: break;
    }
  });

  return { blocks, width };
};
